import {
  BellOutlined,
  CheckOutlined,
  MessageOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Empty,
  Popover,
  Segmented,
  Skeleton,
  message,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { route } from '../../../../../constants/routes';
import {
  RecruiterNotification,
  RecruiterNotificationGroup,
  RecruiterNotificationType,
} from '../models/recruiter-notification.model';
import { RecruiterNotificationService } from '../services/recruiter-notification.service';
import {
  formatNotificationTime,
  groupRecruiterNotifications,
} from '../utils/recruiter-notification-groups';
import './RecruiterNotificationCenter.css';

type NotificationFilter = 'ALL' | RecruiterNotificationType;

const POLL_INTERVAL_MS = 60_000;

const candidateSummary = (names: string[]): string => {
  if (names.length <= 2) return names.join(' and ');
  return `${names[0]}, ${names[1]}, and ${names.length - 2} more`;
};

const RecruiterNotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>('ALL');
  const [items, setItems] = useState<RecruiterNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadApplicationCount, setUnreadApplicationCount] = useState(0);
  const [unreadClarificationCount, setUnreadClarificationCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const feed = await RecruiterNotificationService.feed();
      setItems((feed.items || []).filter((item) => !item.readAt));
      setUnreadCount(feed.unreadCount || 0);
      setUnreadApplicationCount(feed.unreadApplicationCount || 0);
      setUnreadClarificationCount(feed.unreadClarificationCount || 0);
      setUnreadMessageCount(feed.unreadMessageCount || 0);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const interval = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [load]);

  const groups = useMemo(() => groupRecruiterNotifications(items), [items]);
  const filteredGroups = useMemo(
    () =>
      filter === 'ALL'
        ? groups
        : groups.filter((group) => group.type === filter),
    [filter, groups]
  );
  const markGroupRead = async (group: RecruiterNotificationGroup) => {
    const unreadIds = group.notifications
      .filter((notification) => !notification.readAt)
      .map((notification) => notification.id);
    if (!unreadIds.length) return;
    await Promise.all(
      unreadIds.map((id) => RecruiterNotificationService.markRead(id))
    );
    setItems((current) =>
      current.filter((item) => !unreadIds.includes(item.id))
    );
    setUnreadCount((current) => Math.max(0, current - unreadIds.length));
    if (group.type === 'NEW_APPLICATION') {
      setUnreadApplicationCount((current) =>
        Math.max(0, current - unreadIds.length)
      );
    } else if (group.type === 'CLARIFICATION_ANSWERS') {
      setUnreadClarificationCount((current) =>
        Math.max(0, current - unreadIds.length)
      );
    } else {
      setUnreadMessageCount((current) =>
        Math.max(0, current - unreadIds.length)
      );
    }
  };

  const openGroup = async (group: RecruiterNotificationGroup) => {
    try {
      await markGroupRead(group);
      setOpen(false);
      if (group.type === 'CLARIFICATION_ANSWERS') {
        navigate(
          route(
            `/job-applications/${group.applicationId}?tab=clarifications&clarification=${group.clarificationRequestId}`
          ),
          { state: { fromNotifications: true } }
        );
        return;
      }
      if (group.type === 'CANDIDATE_MESSAGE') {
        navigate(
          route(
            `/job-applications/${group.applicationId}?tab=messages&message=${group.candidateMessageId}`
          ),
          { state: { fromNotifications: true } }
        );
        return;
      }
      if (group.notifications.length > 1) {
        navigate(route(`/job/${group.jobId}/job-applications`), {
          state: {
            jobId: group.jobId,
            jobName: group.jobTitle,
            fromNotifications: true,
          },
        });
        return;
      }
      navigate(route(`/job-applications/${group.applicationId}`), {
        state: {
          jobId: group.jobId,
          jobName: group.jobTitle,
          fromNotifications: true,
        },
      });
    } catch {
      message.error('Could not open this notification. Please try again.');
    }
  };

  const markAllRead = async () => {
    if (!unreadCount) return;
    setMarkingAll(true);
    try {
      await RecruiterNotificationService.markAllRead();
      setItems([]);
      setUnreadCount(0);
      setUnreadApplicationCount(0);
      setUnreadClarificationCount(0);
      setUnreadMessageCount(0);
    } catch {
      message.error('Could not mark notifications as read.');
    } finally {
      setMarkingAll(false);
    }
  };

  const notificationContent = (
    <section className="recruiter-notifications" aria-label="Notifications">
      <header className="recruiter-notifications__header">
        <div>
          <strong>Notifications</strong>
          <span aria-live="polite">
            {unreadCount ? `${unreadCount} unread` : 'You’re all caught up'}
          </span>
        </div>
        <Button
          type="link"
          size="small"
          icon={<CheckOutlined />}
          disabled={!unreadCount}
          loading={markingAll}
          onClick={() => void markAllRead()}
        >
          Mark all read
        </Button>
      </header>
      <Segmented<NotificationFilter>
        block
        size="small"
        value={filter}
        onChange={setFilter}
        options={[
          { label: `All · ${unreadCount}`, value: 'ALL' },
          {
            label: `Applications · ${unreadApplicationCount}`,
            value: 'NEW_APPLICATION',
          },
          {
            label: `Answers · ${unreadClarificationCount}`,
            value: 'CLARIFICATION_ANSWERS',
          },
          {
            label: `Messages · ${unreadMessageCount}`,
            value: 'CANDIDATE_MESSAGE',
          },
        ]}
        aria-label="Filter notifications"
        className="recruiter-notifications__filters"
      />
      <div className="recruiter-notifications__list">
        {loading ? (
          <div className="recruiter-notifications__loading" role="status">
            <Skeleton active avatar paragraph={{ rows: 2 }} />
            <span className="sr-only">Loading notifications</span>
          </div>
        ) : loadFailed ? (
          <div className="recruiter-notifications__state" role="status">
            <span>Notifications could not be loaded.</span>
            <Button size="small" onClick={() => void load(true)}>
              Try again
            </Button>
          </div>
        ) : filteredGroups.length ? (
          filteredGroups.map((group) => {
            const isApplication = group.type === 'NEW_APPLICATION';
            const isMessage = group.type === 'CANDIDATE_MESSAGE';
            const title = isApplication
              ? group.notifications.length > 1
                ? `${group.notifications.length} new applications`
                : `New application from ${group.candidateNames[0]}`
              : isMessage
                ? `New message from ${group.candidateNames[0]}`
                : `${group.candidateNames[0]} answered ${group.itemCount} clarification question${group.itemCount === 1 ? '' : 's'}`;
            const description = isApplication
              ? group.notifications.length > 1
                ? `${candidateSummary(group.candidateNames)} · ${group.jobTitle}`
                : group.jobTitle
              : isMessage
                ? `${group.jobTitle} · Open conversation`
                : `${group.jobTitle} · Review answers`;
            return (
              <button
                type="button"
                key={group.key}
                className={`recruiter-notification${group.read ? '' : ' recruiter-notification--unread'}`}
                onClick={() => void openGroup(group)}
              >
                {!group.read ? (
                  <span
                    className="recruiter-notification__unread-dot"
                    aria-label="Unread"
                  />
                ) : null}
                <span
                  className={`recruiter-notification__icon${isApplication ? '' : ' recruiter-notification__icon--answer'}`}
                  aria-hidden="true"
                >
                  {isApplication ? <UserAddOutlined /> : <MessageOutlined />}
                </span>
                <span className="recruiter-notification__content">
                  <strong>{title}</strong>
                  <span>{description}</span>
                </span>
                <time dateTime={group.createdAt}>
                  {formatNotificationTime(group.createdAt)}
                </time>
              </button>
            );
          })
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              filter === 'ALL'
                ? 'No unread notifications'
                : 'No unread notifications in this category'
            }
          />
        )}
      </div>
    </section>
  );

  return (
    <Popover
      content={notificationContent}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) void load();
      }}
      overlayClassName="recruiter-notifications-popover"
    >
      <Badge count={unreadCount} overflowCount={99} size="small">
        <button
          type="button"
          className={`recruiter-notifications-trigger${open ? ' recruiter-notifications-trigger--open' : ''}`}
          aria-label={
            unreadCount
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications'
          }
          aria-expanded={open}
        >
          <BellOutlined />
        </button>
      </Badge>
    </Popover>
  );
};

export default RecruiterNotificationCenter;
